import { useCallback, useEffect, useRef, useState } from "react";
import { uploadInterviewRecording } from "../services/recordingService";
import {
  buildRecordingConstraints,
  getSupportedRecordingMimeType,
  stopMediaStream
} from "../utils/mediaRecorder";

export default function useInterviewRecorder({
  enabled,
  jobId,
  interviewId,
  onUploaded
}) {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingResult, setRecordingResult] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(null);
  const lastBlobRef = useRef(null);
  const audioCtxRef = useRef(null);
  const mixDestinationRef = useRef(null);

  const uploadBlob = useCallback(async (blob, duration) => {
    if (!blob || !enabled || !jobId || !interviewId) {
      return null;
    }

    setStatus("uploading");
    setUploadProgress(0);
    const result = await uploadInterviewRecording({
      blob,
      jobId,
      interviewId,
      duration,
      onProgress: setUploadProgress
    });
    setRecordingResult(result);
    setStatus("uploaded");
    onUploaded?.(result);
    return result;
  }, [enabled, interviewId, jobId, onUploaded]);

  // Call this for every TTS <audio> element to mix system voice into the recording
  const connectTTSAudio = useCallback((audioEl) => {
    if (!audioCtxRef.current || !mixDestinationRef.current) return;
    try {
      if (audioCtxRef.current.state === "suspended") {
        audioCtxRef.current.resume();
      }
      const source = audioCtxRef.current.createMediaElementSource(audioEl);
      source.connect(audioCtxRef.current.destination); // still plays through speakers
      source.connect(mixDestinationRef.current);       // also captured in the recording
    } catch {
      // element already connected to this context — safe to ignore
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!enabled || recorderRef.current || !jobId || !interviewId) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError("Recording is not supported in this browser.");
      setStatus("unsupported");
      return;
    }

    try {
      setError("");
      setStatus("requesting-permission");
      const stream = await navigator.mediaDevices.getUserMedia(buildRecordingConstraints());
      streamRef.current = stream;
      setMediaStream(stream);
      chunksRef.current = [];

      // Set up AudioContext to mix microphone + TTS system audio
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const mixDestination = audioCtx.createMediaStreamDestination();
      audioCtxRef.current = audioCtx;
      mixDestinationRef.current = mixDestination;

      // Route mic audio into the mix
      const micSource = audioCtx.createMediaStreamSource(stream);
      micSource.connect(mixDestination);

      // Build combined stream: webcam video + mixed audio (mic + TTS)
      const combinedStream = new MediaStream([
        ...stream.getVideoTracks(),
        ...mixDestination.stream.getAudioTracks()
      ]);

      const mimeType = getSupportedRecordingMimeType();
      const recorder = new MediaRecorder(combinedStream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        setError("Recording failed. The interview can continue.");
        setStatus("failed");
      };

      recorder.onstop = async () => {
        const duration = startedAtRef.current
          ? Math.round((Date.now() - startedAtRef.current) / 1000)
          : 0;
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "video/webm" });
        lastBlobRef.current = blob;
        chunksRef.current = [];
        recorderRef.current = null;
        stopMediaStream(streamRef.current);
        streamRef.current = null;
        setMediaStream(null);

        try {
          audioCtxRef.current?.close();
        } catch {}
        audioCtxRef.current = null;
        mixDestinationRef.current = null;

        if (!blob.size) {
          setError("Recording ended without video data.");
          setStatus("failed");
          return;
        }

        try {
          await uploadBlob(blob, duration);
        } catch (err) {
          console.error("Recording upload failed:", err);
          setError(err.message || "Recording upload failed.");
          setStatus("upload-failed");
        }
      };

      recorder.start(5000);
      setStatus("recording");
    } catch (err) {
      console.error("Unable to start recording:", err);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setError("Camera and microphone permission is required for proctored interviews.");
        setStatus("permission-denied");
      } else {
        setError(err.message || "Unable to start recording.");
        setStatus("failed");
      }
      stopMediaStream(streamRef.current);
      streamRef.current = null;
      setMediaStream(null);
      recorderRef.current = null;
    }
  }, [enabled, interviewId, jobId, uploadBlob]);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) {
      return;
    }

    setStatus("stopping");
    try {
      if (recorder.state !== "inactive") {
        recorder.requestData();
        recorder.stop();
      }
    } catch (err) {
      console.error("Unable to stop recording:", err);
      setError("Unable to stop recording cleanly.");
      setStatus("failed");
      stopMediaStream(streamRef.current);
      streamRef.current = null;
      setMediaStream(null);
      recorderRef.current = null;
      try {
        audioCtxRef.current?.close();
      } catch {}
      audioCtxRef.current = null;
      mixDestinationRef.current = null;
    }
  }, []);

  const retryUpload = useCallback(async () => {
    if (!lastBlobRef.current) {
      setError("No recording is available to retry.");
      return;
    }

    try {
      setError("");
      const duration = startedAtRef.current
        ? Math.round((Date.now() - startedAtRef.current) / 1000)
        : 0;
      await uploadBlob(lastBlobRef.current, duration);
    } catch (err) {
      console.error("Recording upload retry failed:", err);
      setError(err.message || "Recording upload retry failed.");
      setStatus("upload-failed");
    }
  }, [uploadBlob]);

  useEffect(() => {
    return () => {
      if (recorderRef.current?.state === "recording") {
        try {
          recorderRef.current.stop();
        } catch {}
      }
      stopMediaStream(streamRef.current);
      setMediaStream(null);
      try {
        audioCtxRef.current?.close();
      } catch {}
      audioCtxRef.current = null;
      mixDestinationRef.current = null;
    };
  }, []);

  return {
    status,
    error,
    uploadProgress,
    recordingUrl: recordingResult?.recordingUrl || "",
    mediaStream,
    startRecording,
    stopRecording,
    retryUpload,
    connectTTSAudio
  };
}
